// SMS marketing opt-in shared core — pure TS, no imports.
// Keep in sync with cruises-next/lib/domain/sms-opt-in.ts
// (intentionally duplicated across repos; edit both together and bump
// SMS_CONSENT_TEXT_VERSION whenever the consent copy changes).

export const SMS_CONSENT_TEXT_VERSION = '2026-10-footer-v2' // must match /^[\w.-]{1,64}$/

// The standalone SMS-only modal (no email capture) renders the same
// self-contained consent copy but is versioned separately so opt-ins from that
// path are distinguishable in reporting/audit (GSE story: distinct
// consent_text_version). Bump alongside SMS_CONSENT_TEXT_VERSION if the copy
// changes; both must match /^[\w.-]{1,64}$/.
export const SMS_ONLY_CONSENT_TEXT_VERSION = '2026-10-sms-only-v2'

// Plain string — each repo wraps it in its own gql tag.
export const RECORD_SMS_OPT_IN_MUTATION = `
  mutation RecordSmsOptIn($input: SmsOptInInput!) {
    recordSmsOptIn(input: $input) {
      success
      message
      errors {
        message
      }
    }
  }
`

export type SmsOptInSource =
  | 'LANDING_PAGE'
  | 'WELCOME_MODAL'
  | 'FOOTER'
  | 'BOOKING'
  | 'ORDER_CONFIRMATION'
  | 'ACCOUNT_SIGNUP'
  | 'MY_ACCOUNT'
  | 'SMS_ONLY_MODAL'

// The consent copy shown is the same across surfaces, but the standalone
// SMS-only path is versioned separately (see SMS_ONLY_CONSENT_TEXT_VERSION).
export const consentVersionForSource = (source: SmsOptInSource): string =>
  source === 'SMS_ONLY_MODAL'
    ? SMS_ONLY_CONSENT_TEXT_VERSION
    : SMS_CONSENT_TEXT_VERSION

export interface SmsOptInInput {
  phone: string
  smsSource: SmsOptInSource
  consentTextVersion: string
  email?: string
  anonymousId?: string
  // The signed-in user's id, when there is one; the backend keys the record
  // on it. A logged-out opt-in sends none and is tied server-side to the
  // account that later signs up or logs in with the same phone number
  // (GSE-12330).
  externalId?: string
}

export interface RecordSmsOptInResult {
  success: boolean
  message: string
  errors: { message: string }[]
}

// Mirrors the backend's Services::Phone::E164 for US numbers: accepts
// 10-digit, 11-digit-with-leading-1, or +1-prefixed input in any punctuation;
// NANP requires the area code AND exchange to start [2-9]. Returns the
// canonical '+1XXXXXXXXXX' or null when the input is not a valid US number.
export function normalizeUsPhone(raw: string): string | null {
  const trimmed = String(raw ?? '').trim()
  if (trimmed.startsWith('+') && !trimmed.startsWith('+1')) return null
  const digits = trimmed.replace(/\D/g, '')
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(national)) return null
  return `+1${national}`
}

export const isValidUsPhone = (raw: string): boolean =>
  normalizeUsPhone(raw) !== null

// Consent copy. '{brand}' is replaced with the brand display name at render
// time; the fine print is segmented so each repo can wrap the link labels in
// its own anchor elements while the legal copy stays byte-identical.
// Both surfaces that show this modal follow an email signup — the Braze
// welcome modal and the footer newsletter — so both acknowledge it rather than
// re-pitching the list. The lead is navy and the accent brand-green.
export const SMS_OPT_IN_TITLE_LEAD = 'You’re'
export const SMS_OPT_IN_TITLE_ACCENT = 'In!'
export const SMS_OPT_IN_TITLE = `${SMS_OPT_IN_TITLE_LEAD} ${SMS_OPT_IN_TITLE_ACCENT}`
export const SMS_OPT_IN_SUBTITLE =
  'Enter your phone number for exclusive offers, early access to promotions, and travel tips.'
export const SMS_CONSENT_CHECKBOX_COPY =
  'By entering the phone number and checking the box, I agree to receive marketing SMS messages, information, and offers from {brand}.¹'
export const smsConsentCheckboxCopy = (brandName: string): string =>
  SMS_CONSENT_CHECKBOX_COPY.replace('{brand}', brandName)
export const SMS_FINE_PRINT = {
  body: '¹ US Phone Numbers only. Consent is not a condition of purchase. Message and data rates may apply. Message frequency varies. Reply HELP for help or STOP to cancel. For additional information, view our ',
  termsLabel: 'terms/conditions',
  conjunction: ' and ',
  privacyLabel: 'privacy policy',
  suffix: '.',
} as const
